"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth } from "../context/AuthContext"
import Button from "./Button"

const TokenExpiryNotification = () => {
  const [showWarning, setShowWarning] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const [autoLogoutTimer, setAutoLogoutTimer] = useState(null)
  const { token, user, login, logout } = useAuth()

  // Auto logout function
  const performAutoLogout = useCallback(() => {
    console.log("Auto logout triggered - user did not extend session")
    setShowWarning(false)
    logout()
  }, [logout])

  useEffect(() => {
    let countdownInterval = null

    const handleTokenExpiring = (event) => {
      const { timeUntilExpiry } = event.detail
      const secondsLeft = Math.floor(timeUntilExpiry / 1000)

      console.log("Token expiring event received, seconds left:", secondsLeft)

      setShowWarning(true)
      setTimeLeft(secondsLeft)

      // Clear any existing auto logout timer
      if (autoLogoutTimer) {
        clearTimeout(autoLogoutTimer)
      }

      // Set auto logout timer for when countdown reaches 0
      const newAutoLogoutTimer = setTimeout(() => {
        performAutoLogout()
      }, timeUntilExpiry)
      setAutoLogoutTimer(newAutoLogoutTimer)

      // Start countdown display
      countdownInterval = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1
          if (newTime <= 0) {
            clearInterval(countdownInterval)
            // Don't call logout here - let the setTimeout handle it
            return 0
          }
          return newTime
        })
      }, 1000)
    }

    const handleTokenExpired = () => {
      console.log("Token expired event received")
      setShowWarning(false)
      if (countdownInterval) {
        clearInterval(countdownInterval)
      }
      if (autoLogoutTimer) {
        clearTimeout(autoLogoutTimer)
        setAutoLogoutTimer(null)
      }
    }

    const handleUserLoggedOut = () => {
      console.log("User logged out event received")
      setShowWarning(false)
      if (countdownInterval) {
        clearInterval(countdownInterval)
      }
      if (autoLogoutTimer) {
        clearTimeout(autoLogoutTimer)
        setAutoLogoutTimer(null)
      }
    }

    // Listen for token expiration events
    window.addEventListener("tokenExpiring", handleTokenExpiring)
    window.addEventListener("tokenExpired", handleTokenExpired)
    window.addEventListener("userLoggedOut", handleUserLoggedOut)

    return () => {
      window.removeEventListener("tokenExpiring", handleTokenExpiring)
      window.removeEventListener("tokenExpired", handleTokenExpired)
      window.removeEventListener("userLoggedOut", handleUserLoggedOut)
      if (countdownInterval) {
        clearInterval(countdownInterval)
      }
      if (autoLogoutTimer) {
        clearTimeout(autoLogoutTimer)
        setAutoLogoutTimer(null)
      }
    }
  }, [autoLogoutTimer, performAutoLogout])

  const handleExtendSession = async () => {
    try {
      console.log("User clicked extend session")

      // Clear the auto logout timer while we refresh
      if (autoLogoutTimer) {
        clearTimeout(autoLogoutTimer)
        setAutoLogoutTimer(null)
      }

      // Call backend to refresh the token (adjust endpoint as needed)
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })

      if (!res.ok) {
        throw new Error(`Refresh failed with status ${res.status}`)
      }

      const data = await res.json().catch(() => ({}))
      const newToken = data.token || data.accessToken
      const newUser = data.user || user

      if (!newToken) {
        throw new Error("No token returned from refresh endpoint")
      }

      // Persist refreshed session
      login(newUser, newToken)

      // Hide dialog; the AuthContext poller will re-warn later if needed
      setShowWarning(false)
      console.log("Session extended successfully")
    } catch (error) {
      console.error("Failed to extend session:", error)
      logout()
    }
  }


  const handleLogoutNow = () => {
    console.log("User chose to logout immediately")
    if (autoLogoutTimer) {
      clearTimeout(autoLogoutTimer)
      setAutoLogoutTimer(null)
    }
    setShowWarning(false)
    logout()
  }

  if (!showWarning || !token) return null

  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  return (
    <div className="popup-backdrop" style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.35)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <div className={`popup-container ${showWarning ? "popup-show" : ""}`} style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
        width: "min(520px, 92vw)",
        maxWidth: "520px",
        position: "relative"
      }}>
        <div className="popup-header" style={{
          padding: "1rem 1.25rem",
          borderBottom: "1px solid #e2e8f0",
          background: "#ffffff"
        }}>
          <div className="header-content" style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <svg className="warning-icon" fill="#E4D6F6" viewBox="0 0 20 20" style={{ width: 24, height: 24 }}>
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 style={{ margin: 0, color: "#1e293b", fontWeight: 600 }}>Session Expiring Soon</h3>
              <p style={{ margin: 0, color: "#64748b" }}>You will be logged out automatically</p>
            </div>
          </div>
        </div>
        <div className="popup-content" style={{ padding: "1rem 1.25rem" }}>
          <div className="countdown" style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <div className="timer-circle" style={{
              width: 72,
              height: 72,
              borderRadius: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              color: "#1e293b",
              fontWeight: 600
            }}>
              <span>
                {minutes}:{seconds.toString().padStart(2, "0")}
              </span>
            </div>
            <p style={{ margin: 0, color: "#64748b" }}>Time remaining until automatic logout</p>
            
          </div>
          <div className="progress-bar" style={{
            marginTop: "0.75rem",
            height: 8,
            background: "#f1f5f9",
            borderRadius: 9999,
            overflow: "hidden"
          }}>
            <div className="progress-fill" style={{ width: `${Math.max(0, (timeLeft / 120) * 100)}%`, height: "100%", background: "#A78BFA" }} />
          </div>
          <div className="action-buttons" style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", alignItems: "center" }}>
            <Button variant="primary" onClick={handleExtendSession}>Continue Session</Button>
            <Button variant="secondary" onClick={handleLogoutNow}>Logout Now</Button>
          </div>
        </div>
        <div className="popup-footer" style={{ padding: "0.75rem 1.25rem", borderTop: "1px solid #e2e8f0", color: "#64748b" }}>
          <p style={{ margin: 0 }}>For security reasons, inactive sessions are automatically terminated after the countdown expires</p>
        </div>
      </div>
    </div>
  )
}

export default TokenExpiryNotification
